import BeMaccaferriIdentityProvider from './BeMaccaferriIdentityProvider';
import MaccaferriIdentityProvider from './MaccaferriIdentityProvider';
import { IGetUserNameResponse, IGetUserEmailResponse } from '../IApiResponse';
import { IResponse } from '../../../apiService/httpClient';

/**
 * Get user name with Identity API priority and backend fallback.
 *
 * Flow:
 * 1. Try direct call to identity.maccaferri.com/Identity/GetUser
 * 2. If fails, fallback to backend /api/v1/macroidentity/current-user-name
 *
 * This ensures we get real IsAdmin/Role values from Maccaferri Identity's
 * user database, not just the JWT claim defaults.
 */
export const getUserNameAsync = async (): Promise<IResponse<IGetUserNameResponse>> => {
    try {
        const identityResponse = await MaccaferriIdentityProvider.getUserAsync();

        if (identityResponse.status === 200 && identityResponse.data) {
            console.log('[Identity] Direct API call successful');
            const mappedData: IGetUserNameResponse = {
                first_Name: identityResponse.data.firstName,
                last_Name: identityResponse.data.lastName,
                isAdmin: identityResponse.data.isAdmin ? 1 : 0,
            };
            return {
                ...identityResponse,
                data: mappedData,
            };
        }
    } catch (error) {
        console.warn('[Identity] Direct API failed, falling back to backend:', error);
    }

    console.log('[Identity] Using backend fallback');
    return BeMaccaferriIdentityProvider.getUserNameAsync();
};

export const getUserEmailAsync = async (): Promise<IResponse<IGetUserEmailResponse>> => {
    try {
        const identityResponse = await MaccaferriIdentityProvider.getUserAsync();

        if (identityResponse.status === 200 && identityResponse.data) {
            console.log('[Identity] Direct API call successful for email');
            const mappedData: IGetUserEmailResponse = {
                email: identityResponse.data.email,
                isAdmin: identityResponse.data.isAdmin ? 1 : 0,
            };
            return {
                ...identityResponse,
                data: mappedData,
            };
        }
    } catch (error) {
        console.warn('[Identity] Direct API failed for email, falling back to backend:', error);
    }

    return BeMaccaferriIdentityProvider.getUserEmailAsync();
};

export const logoutAsync = async (): Promise<IResponse<boolean>> => {
    try {
        await MaccaferriIdentityProvider.logoutAsync();
        console.log('[Identity] Direct logout successful');
    } catch (error) {
        console.warn('[Identity] Direct logout failed:', error);
    }

    let response: IResponse<boolean> = { data: false, status: 500, statusText: 'Logout failed' } as IResponse<boolean>;
    try {
        response = await BeMaccaferriIdentityProvider.logoutAsync();
    } catch (error) {
        console.warn('[Identity] Backend logout failed:', error);
    }

    // Always redirect after both logout attempts complete
    window.location.href = process.env.REACT_APP_EDESIGN_PORTAL_URL || '';
    return response;
};
